// CB:BEGIN homeowner-documents-page
/**
 * Documents page for homeowner: signed contract, invoice, accepted quote per project.
 * Also surfaces additional project docs (warranty, permit, change order, completion cert, lien waiver).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../lib/useApi';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import ResponsiveContainer from '../components/layout/ResponsiveContainer';
import SharedHeaderCustomer from '../components/SharedHeaderCustomer';

interface ProjectDocuments {
  projectId: string;
  address: string;
  documents: {
    signedContractUrl: string | null;
    signedContractLabel: string;
    invoiceUrl: string | null;
    invoiceLabel: string;
    bidComparisonUrl: string | null;
    bidComparisonLabel: string;
    proposalBidId: string | null;
    invoiceStatus: string | null;
    invoiceReadableId: string | null;
    contractorAgreementUrl: string | null;
  };
}

// CB:BEGIN homeowner-additional-docs
// Additional project docs that are homeowner-visible but not surfaced by /api/homeowner/documents
const ADDITIONAL_DOC_TYPES = new Set([
  'WARRANTY', 'PERMIT', 'FINAL_COMPLETION', 'COMPLETION_CERTIFICATE',
  'LIEN_WAIVER', 'CHANGE_ORDER', 'PROJECT_CHANGE_ORDER',
  'HOMEOWNER_CONTRACT_SIGNED_MANUAL', 'MILESTONE_1_EVIDENCE',
  'MILESTONE_2_EVIDENCE', 'MILESTONE_3_EVIDENCE',
]);

const ADDITIONAL_DOC_LABELS: Record<string, string> = {
  WARRANTY: 'Warranty',
  PERMIT: 'Permit',
  FINAL_COMPLETION: 'Final Completion',
  COMPLETION_CERTIFICATE: 'Completion Certificate',
  LIEN_WAIVER: 'Lien Waiver',
  CHANGE_ORDER: 'Change Order',
  PROJECT_CHANGE_ORDER: 'Change Order',
  HOMEOWNER_CONTRACT_SIGNED_MANUAL: 'Signed Contract',
  MILESTONE_1_EVIDENCE: 'Milestone 1 Evidence',
  MILESTONE_2_EVIDENCE: 'Milestone 2 Evidence',
  MILESTONE_3_EVIDENCE: 'Milestone 3 Evidence',
};

interface AdditionalDoc {
  id: string | null;
  type: string;
  title: string;
  downloadUrl: string | null;
  fileName?: string | null;
}
// CB:END homeowner-additional-docs

const INVOICE_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT:             { label: 'Draft',            color: '#6b7280' },
  ISSUED:            { label: 'Issued',           color: '#2563eb' },
  PENDING_FUNDING:   { label: 'Funding Required', color: '#d97706' },
  FINANCING_PENDING: { label: 'Financing Pending',color: '#7c3aed' },
  FINANCED:          { label: 'Financed',         color: '#059669' },
  FUNDED:            { label: 'Funded',           color: '#059669' },
  IN_PROGRESS:       { label: 'In Progress',      color: '#2563eb' },
  PARTIALLY_PAID:    { label: 'Partially Paid',   color: '#2563eb' },
  DISPUTED:          { label: 'Disputed',         color: '#dc2626' },
  RESOLVED:          { label: 'Resolved',         color: '#059669' },
  COMPLETED:         { label: 'Completed',        color: '#059669' },
  PAID:              { label: 'Paid',             color: '#059669' },
  REFUNDED:          { label: 'Refunded',         color: '#6b7280' },
  VOID:              { label: 'Void',             color: '#6b7280' },
  FAILED:            { label: 'Failed',           color: '#dc2626' },
};

const DocButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    className="btn btn-primary"
    style={{ fontSize: 14, width: 'auto', whiteSpace: 'nowrap' }}
    onClick={onClick}
  >
    {label}
  </button>
);

const HomeownerDocumentsPage: React.FC<{ onNavigate: (screen: string) => void }> = ({ onNavigate }) => {
  const { get } = useApi();
  const authFetch = useAuthFetch();
  const [projects, setProjects] = useState<ProjectDocuments[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  // CB:BEGIN homeowner-additional-docs-state
  const [additionalDocs, setAdditionalDocs] = useState<Record<string, AdditionalDoc[]>>({});
  const [additionalExpanded, setAdditionalExpanded] = useState<Record<string, boolean>>({});
  const [additionalLoading, setAdditionalLoading] = useState<Record<string, boolean>>({});
  // CB:END homeowner-additional-docs-state

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get<{ projects: ProjectDocuments[] }>('/api/homeowner/documents');
        if (!cancelled && res?.projects) setProjects(res.projects);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [get]);

  // CB:BEGIN homeowner-additional-docs-fetch
  const loadAdditionalDocs = useCallback(async (projectId: string) => {
    if (additionalDocs[projectId] !== undefined) return; // already loaded
    setAdditionalLoading(prev => ({ ...prev, [projectId]: true }));
    try {
      const res = await authFetch(getApiUrl(`/api/projects/${projectId}/documents`));
      if (!res.ok) return;
      const data = await res.json();
      const rawDocs: any[] = data.documents ?? [];
      const filtered: AdditionalDoc[] = rawDocs
        .filter((d: any) => ADDITIONAL_DOC_TYPES.has(d.type))
        .map((d: any) => ({
          id: d.id,
          type: d.type,
          title: ADDITIONAL_DOC_LABELS[d.type] ?? d.title ?? d.type.replace(/_/g, ' '),
          downloadUrl: d.downloadUrl ?? null,
          fileName: d.fileName ?? null,
        }));
      setAdditionalDocs(prev => ({ ...prev, [projectId]: filtered }));
    } catch { /* non-fatal */ } finally {
      setAdditionalLoading(prev => ({ ...prev, [projectId]: false }));
    }
  }, [authFetch, additionalDocs]);

  const toggleAdditional = useCallback((projectId: string) => {
    const willExpand = !additionalExpanded[projectId];
    setAdditionalExpanded(prev => ({ ...prev, [projectId]: willExpand }));
    if (willExpand) loadAdditionalDocs(projectId);
  }, [additionalExpanded, loadAdditionalDocs]);
  // CB:END homeowner-additional-docs-fetch

  const openSignedUrl = (url: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openProposalPdf = async (projectId: string, bidId: string) => {
    const key = `${projectId}:${bidId}`;
    if (openingId === key) return;
    setOpeningId(key);
    try {
      const url = getApiUrl(`/api/projects/${projectId}/bids/${bidId}/quote-pdf`);
      const res = await authFetch(url);
      if (!res.ok) return;
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      console.error('Failed to open proposal:', e);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <ResponsiveContainer className="container">
      <SharedHeaderCustomer onNavigate={onNavigate} />
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Documents</h1>
          <p style={{ color: '#6b7280', fontSize: 16 }}>
            View and download your signed contract, invoice, and accepted quote by project.
          </p>
        </div>

        {loading && (
          <p style={{ color: '#6b7280' }}>Loading documents…</p>
        )}
        {error && (
          <p style={{ color: '#dc2626' }}>{error}</p>
        )}
        {!loading && !error && projects.length === 0 && (
          <div className="dashboard-card" style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ color: '#6b7280', margin: 0 }}>
              You don't have any projects with accepted bids yet. Once you accept a bid, your invoice and contract will appear here.
            </p>
          </div>
        )}
        {!loading && !error && projects.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {projects.map((p) => (
              <div key={p.projectId} className="dashboard-card" style={{ padding: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{p.address || `Project ${p.projectId}`}</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                  {p.documents.signedContractUrl && (
                    <DocButton
                      label={`View ${p.documents.signedContractLabel}`}
                      onClick={() => openSignedUrl(p.documents.signedContractUrl)}
                    />
                  )}
                  {p.documents.invoiceUrl && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DocButton
                        label={`View ${p.documents.invoiceLabel}`}
                        onClick={() => openSignedUrl(p.documents.invoiceUrl)}
                      />
                      {p.documents.invoiceStatus && INVOICE_STATUS_LABELS[p.documents.invoiceStatus] && (
                        <span style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 12,
                          backgroundColor: `${INVOICE_STATUS_LABELS[p.documents.invoiceStatus].color}1a`,
                          color: INVOICE_STATUS_LABELS[p.documents.invoiceStatus].color,
                          whiteSpace: 'nowrap',
                        }}>
                          {INVOICE_STATUS_LABELS[p.documents.invoiceStatus].label}
                        </span>
                      )}
                    </div>
                  )}
                  {!p.documents.invoiceUrl && p.documents.invoiceStatus && ['PENDING_FUNDING', 'FINANCING_PENDING'].includes(p.documents.invoiceStatus) && (
                    <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                      <strong>Funding required:</strong> Your invoice is ready. Log in to fund your project escrow or apply for financing through Enhancify.
                    </div>
                  )}
                  {p.documents.bidComparisonUrl && (
                    <DocButton
                      label="View Bid Comparison"
                      onClick={() => openSignedUrl(p.documents.bidComparisonUrl)}
                    />
                  )}
                  {p.documents.proposalBidId && (
                    <DocButton
                      label={openingId === `${p.projectId}:${p.documents.proposalBidId}` ? 'Opening…' : 'View Winning Proposal'}
                      onClick={() => openProposalPdf(p.projectId, p.documents.proposalBidId!)}
                    />
                  )}
                  {p.documents.contractorAgreementUrl && (
                    <DocButton
                      label="View Contractor Agreement"
                      onClick={() => openSignedUrl(p.documents.contractorAgreementUrl)}
                    />
                  )}
                  {!p.documents.signedContractUrl && !p.documents.invoiceUrl && !p.documents.bidComparisonUrl && !p.documents.proposalBidId && !p.documents.contractorAgreementUrl && (
                    <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>
                      Documents will appear here after you sign the contract and the invoice is generated.
                    </p>
                  )}
                </div>

                {/* CB:BEGIN homeowner-additional-docs-section */}
                <div style={{ marginTop: 16, borderTop: '1px solid #f3f4f6', paddingTop: 14 }}>
                  <button
                    onClick={() => toggleAdditional(p.projectId)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#2563eb', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {additionalExpanded[p.projectId] ? '▾' : '▸'} Project Files (warranty, permits, change orders…)
                  </button>
                  {additionalExpanded[p.projectId] && (
                    <div style={{ marginTop: 12 }}>
                      {additionalLoading[p.projectId] ? (
                        <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>Loading…</p>
                      ) : (additionalDocs[p.projectId]?.length ?? 0) === 0 ? (
                        <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No additional project files yet.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {additionalDocs[p.projectId].map((doc, i) => (
                            <div key={doc.id ?? i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                              <div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{doc.title}</span>
                                {doc.fileName && <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>{doc.fileName}</span>}
                              </div>
                              {doc.downloadUrl ? (
                                <a href={doc.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#2563eb', textDecoration: 'none', padding: '3px 10px', border: '1px solid #bfdbfe', borderRadius: 5 }}>Download</a>
                              ) : (
                                <span style={{ fontSize: 12, color: '#d1d5db' }}>Not available</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* CB:END homeowner-additional-docs-section */}
              </div>
            ))}
          </div>
        )}
      </div>
    </ResponsiveContainer>
  );
};

export default HomeownerDocumentsPage;
// CB:END homeowner-documents-page
